import React from 'react';
import { Button, Divider } from 'antd';
import { BlockOutlined, PlusSquareOutlined } from '@ant-design/icons';
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

function uuidv4() {
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
        (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
    );
}

export default (formPlugin, namespace) =>
{
    const POSITION_VERTICAL = [
        t`Haut`,
        t`Bas`
    ]

    const POSITION_HORIZONTAL = [
        t`Gauche`,
        t`Centre`,
        t`Droite`
    ]

    const stepPlugin = {
        Renderer: (props) => {
            const { data } = props

            // NEUTRALISÉ : le composant "étapes" n'est plus rendu comme un wizard.
            // Chaque étape affiche simplement son contenu inline, à la suite, sans
            // masquage ni navigation. Les formulaires existants restent valides
            // (plugin toujours enregistré) mais se comportent comme un formulaire plat.
            // On CONSERVE toutefois le titre (+ description) de l'étape comme un
            // en-tête de section, sinon le formulaire à plat devient incohérent.
            const liveRender = (props.isPreviewMode || formPlugin.live)

            return (
                <div id={"form-step"} style={{
                    border: liveRender ? "none" : "1px dashed orange",
                    borderRadius: liveRender ? 0 : 10,
                    padding: liveRender ? 0 : "2%"
                }}>
                    {!liveRender && <Divider>({data.title})</Divider>}
                    {liveRender && data?.title && (
                        <div style={{ margin: "8px 0 16px" }}>
                            <Divider orientation="left" style={{ margin: "8px 0" }}>
                                {data.title}
                            </Divider>
                            {data?.description && (
                                <div style={{ color: "rgba(0,0,0,0.45)", marginTop: 4 }}>
                                    {data.description}
                                </div>
                            )}
                        </div>
                    )}
                    {props.children}
                </div>
            )
        },
        namespace: namespace,
        id: 'form-step' + namespace,
        title: t`Étape`,
        description: t`Élément de formulaire utilisable.`,
        icon: <PlusSquareOutlined />,
        iconColor: "#BA4A00",
        version: 1,
        cellPlugins: (plugins, data) => formPlugin.getPluginsMem(namespace).filter(i => !i.id.includes("form-step-container")),
        allowClickInside: true,
        isInlinable: false,
        allowInlineNeighbours: false,
        ignoreLayoutParams: true,
        controls: {
            type: 'autoform',
            schema: {
                properties: {
                    title: {
                        type: 'string',
                        uniforms: {
                            label: t`Titre`,
                        }
                    },
                    description: {
                        type: 'string',
                        uniforms: {
                            label: t`Description`,
                        }
                    }
                }
            },
        },
    }

    var localData = {}

    return {
        Renderer: (props) => {
            const { data } = props
            localData = data

            // NEUTRALISÉ : plus de wizard (composant Steps, navigation Précédent/Suivant).
            // Le conteneur affiche bêtement toutes les étapes inline, à la suite.
            // On conserve UNIQUEMENT un bouton "Valider" final (sauf hideSubmit) :
            // dans les formulaires multi-étapes existants, c'est le seul bouton de
            // soumission (aucun plugin form-submit-button séparé) — le retirer
            // rendrait le formulaire impossible à envoyer.
            const liveRender = (props.isPreviewMode || formPlugin.live)

            var justifyContent = "flex-end"
            const buttonHorizontal = data.button_horizontal_position || POSITION_HORIZONTAL[2]
            switch (buttonHorizontal) {
                case POSITION_HORIZONTAL[0]:
                    justifyContent = "flex-start"
                    break;
                case POSITION_HORIZONTAL[1]:
                    justifyContent = "center"
                    break;
                case POSITION_HORIZONTAL[2]:
                    justifyContent = "flex-end"
                    break;
            }

            return (
                <div id={"step-container-plugin_" + props.nodeId}>
                    {props.children}

                    {liveRender && !data?.hideSubmit && (
                        <div style={{
                            display: "flex",
                            justifyContent: justifyContent,
                            alignItems: "center",
                            margin: "10px 20px"
                        }}>
                            <Button
                                htmlType="submit"
                                size={data.reduced ? "default" : "large"}
                                type="primary"
                                style={{ color: "#fff" }}
                            >
                                <Trans context="form.submit" comment="Submit the completed form; does not imply approval, payment or authentication.">Valider</Trans>
                            </Button>
                        </div>
                    )}
                </div>
            )
        },
        namespace: namespace,
        id: 'form-step-container' + namespace,
        title: t`Étapes`,
        description: t`Élément de formulaire utilisable.`,
        icon: <BlockOutlined />,
        iconColor: "#BA4A00",
        hideInMenu: true,
        version: 1,
        createInitialData: () => {
            return { id: uuidv4(), current: 0 }
        },
        cellPlugins: () => {
            var p = { ...stepPlugin }
            p.createInitialData = () => {
                return { id: uuidv4(), stepId: localData.id }
            }
            return [p]
        },
        cellSpacing: { x: 0, y: 0 },
        ignoreLayoutParams: true,
        controls: {
            type: 'autoform',
            schema: {
                properties: {
                    reduced: {
                        type: 'boolean',
                        uniforms: {
                            label: t`Mini`,
                        }
                    },
                    hideTitle: {
                        type: 'boolean',
                        uniforms: {
                            label: t`Masquer les titres et descriptions`,
                        }
                    },
                    clickable: {
                        type: 'boolean',
                        uniforms: {
                            label: t`Onglets cliquables`,
                        }
                    },
                    button_vertical_position: {
                        type: "string",
                        uniforms: {
                            label: t`Positionnement vertical des boutons`,
                            allowedValues: POSITION_VERTICAL,
                        }
                    },
                    button_horizontal_position: {
                        type: "string",
                        uniforms: {
                            label: t`Positionnement horizontal des boutons`,
                            allowedValues: POSITION_HORIZONTAL,
                        }
                    },
                    hideSubmit: {
                        type: 'boolean',
                        uniforms: {
                            label: t`Masquer le bouton Valider`,
                        }
                    },
                }
            },
        },
    }
}
